import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  User,
  Mail,
  LockKeyhole,
  Eye,
  EyeOff,
  Check,
  Globe,
  Bot,
  Zap,
  Pencil,
  ChevronRight,
  ShieldCheck,
  BadgeCheck,
  CreditCard,
  MessageSquare,
  Users,
  BarChart3,
  Rocket,
  Gift,
  Sparkles,
  Building2,
} from "lucide-react";
import { signupAction, loginAction } from "../../actions/auth";
import { resolveAuthLanding } from "../../utils/authLanding";
import {
  listPlans,
  previewCheckout,
  formatLimit,
  planLabel,
  isUnlimited,
} from "../../actions/billing";
import { TYPE_META, isBlockedPreview } from "../Billing/previewMeta";
import { RoutesPaths } from "../../Router/constant";
import useHelcimPayment from "../Billing/useHelcimPayment";

// Served from public/, so it is a plain URL rather than an import - the same
// convention the logos use, and css-loader cannot resolve a url() that points
// into public/ anyway.
const BRAND_BACKGROUND = {
  backgroundImage: 'url("/assets/bgimage.png")',
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

// The plans live on the marketing site, not in the app: someone still choosing
// has no account to take them to Billing with.
const PRICING_URL = "https://www.hostor.ai/#pricing";

// Why someone is signing up, on the brand panel. Copy, not data.
const SELLING_POINTS = [
  {
    icon: MessageSquare,
    title: "AI Receptionist",
    detail: "Answer, qualify and capture leads 24/7",
  },
  {
    icon: Users,
    title: "More Customers",
    detail: "Boost engagement and conversions",
  },
  {
    icon: BarChart3,
    title: "Grow Faster",
    detail: "Save time and scale your business",
  },
  {
    icon: ShieldCheck,
    title: "Secure & Reliable",
    detail: "Your data is always safe with us",
  },
];

const PLAN_STYLE = {
  free: { icon: Gift, blurb: "For trying Hostor and seeing how it works." },
  starter: {
    icon: Sparkles,
    blurb: "For small businesses that need an AI receptionist on one website.",
  },
  pro: {
    icon: Rocket,
    blurb: "For growing businesses that need Hostor across several websites.",
  },
  agency: {
    icon: Users,
    blurb: "For agencies managing AI receptionists for multiple clients.",
  },
  business: {
    icon: Building2,
    blurb: "For larger organizations needing higher usage and custom workflows.",
  },
};

// The three headline allowances, shown across the top of the plan card.
const HIGHLIGHTS = [
  { key: "maxAssistants", icon: Globe, one: "website", many: "websites" },
  { key: "maxAssistants", icon: Bot, one: "AI assistant", many: "AI assistants" },
  {
    key: "monthlyChatLimitPerAssistant",
    icon: Zap,
    one: "AI chat / month",
    many: "AI chats / month",
  },
];

const DETAILS = [
  { key: "maxTrainingMessages", label: "training messages" },
  { key: "maxVisitorMessages", label: "messages per visitor" },
  { key: "maxKnowledgeSources", label: "pages crawled (whole account)" },
  { key: "maxRecrawlsPerMonth", label: "re-crawls / month per assistant" },
  { key: "maxLeadsPerMonth", label: "leads / month" },
];

// "2026-09-23" -> "23 Sep 2026", read as a plain date rather than a moment:
// through new Date() a bare date is UTC midnight and shifts a day west of GMT.
const plainDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
    undefined,
    { day: "numeric", month: "short", year: "numeric" },
  );
};

const money = (value) => {
  if (typeof value !== "number") return null;
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

const signedMoney = (value) => {
  if (typeof value !== "number") return null;
  return value < 0 ? `- ${money(Math.abs(value))}` : money(value);
};

const allowance = (value, one, many) => {
  if (isUnlimited(value)) return `Unlimited ${many}`;
  if (typeof value !== "number") return null;
  return `${value.toLocaleString()} ${value === 1 ? one : many}`;
};

/**
 * Sign-up with the chosen plan alongside it: the account is created here and
 * the browser goes on to pay. The plan and cycle come from the query string,
 * so a pricing page can link straight in.
 */
export default function CheckOut() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const planKey = params.get("plan") || "pro";
  const interval = params.get("interval") === "monthly" ? "monthly" : "annual";
  const annual = interval === "annual";

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  // The server's own costing of this change. Only available to a signed-in
  // visitor - the endpoint needs a session - so the page still has to work
  // without it, which is the common case here.
  const [preview, setPreview] = useState(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Two steps behind one button, so the label can say which one is running.
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  // A 409: this email already has a plan, so the way on is to sign in.
  const [existingAccount, setExistingAccount] = useState(false);
  // The card popup, and the call that confirms what it reports.
  const { pay, stage: payStage, error: payError } = useHelcimPayment();

  const load = useCallback(async () => {
    const [tiers, quote] = await Promise.all([
      dispatch(listPlans()),
      dispatch(previewCheckout(planKey, interval, { guest: true })),
    ]);

    setLoadingPlans(false);

    if (tiers?.success) setPlans(tiers.plans);
    // A 401 here is expected for a visitor who has not signed up yet; the
    // summary falls back to the published prices.
    if (quote?.success) setPreview(quote.preview);
  }, [dispatch, planKey, interval]);

  useEffect(() => {
    load();
  }, [load]);

  const plan = plans.find((entry) => entry.plan === planKey);
  const style = PLAN_STYLE[planKey] || {};
  const PlanIcon = style.icon;

  // Annual is quoted per month with the yearly total below, which is how the
  // plans endpoint reports it.
  const perMonth = annual ? plan?.annualPriceMonthly : plan?.monthlyPrice;
  const cycleTotal = annual ? plan?.annualPrice : plan?.monthlyPrice;
  // The discount is the gap between twelve months at list and the annual
  // price, rather than a percentage stated anywhere.
  const listTotal =
    annual && typeof plan?.monthlyPrice === "number"
      ? plan.monthlyPrice * 12
      : null;
  const discount =
    listTotal !== null && typeof cycleTotal === "number"
      ? listTotal - cycleTotal
      : null;
  const discountPct =
    discount && listTotal ? Math.round((discount / listTotal) * 100) : null;

  // What the quote says the plan itself costs per cycle - the same number the
  // plans endpoint gives, but stated by the thing that will charge it.
  const quotedPlanPrice =
    typeof preview?.to?.price === "number" ? preview.to.price : null;

  // The server's figure wins when it exists: it accounts for proration and
  // tax, which the published price cannot.
  const quotedTotal =
    typeof preview?.chargeToday === "number" ? preview.chargeToday : null;
  const quotedTax = typeof preview?.tax === "number" ? preview.tax : null;
  const totalToday = quotedTotal !== null ? quotedTotal : cycleTotal;

  // What the quote says about the change itself rather than its price: the
  // plans it moves between, how it should be labelled, and whether the server
  // will take it at all.
  const typeMeta = TYPE_META[preview?.type] || {};
  const fromPlan = preview?.from || preview?.currentPlan;
  const toPlan = preview?.to || preview?.newPlan;
  const action = preview?.action || preview?.cta;
  const blocked = isBlockedPreview(preview);
  const lineItems = Array.isArray(preview?.lineItems) ? preview.lineItems : [];
  const calculation = Array.isArray(preview?.calculation)
    ? preview.calculation
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // The server has already refused this change - an over-limit downgrade,
    // say - so there is nothing to sign up for. The reason is in the summary.
    if (blocked) {
      setError(
        preview?.message || "This plan isn't available for your account.",
      );
      return;
    }

    if (!fullName.trim() || !email.trim() || !password.trim()) {
      setError("Please fill in your name, email and password.");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setError("");
    setStage("account");

    // One call: register carries the plan, creates the account and answers
    // with a checkout for the card popup. No token comes back on purpose -
    // the customer signs in after paying.
    const result = await dispatch(
      signupAction({
        email: email.trim(),
        password,
        name: fullName.trim(),
        plan: planKey,
        interval,
      }),
    );

    if (result?.success && result.checkout) {
      setStage("payment");

      // Taken here rather than anywhere else: completing a checkout needs the
      // token, not a session, which is the whole reason this page can finish a
      // payment for an account that cannot sign in yet.
      const payment = await pay(result.checkout);

      setStage("");

      if (payment.done) {
        // Paid, and the account was created a moment ago with the credentials
        // still in this form - so there is no reason to ask for them again.
        setStage("account");

        const credentials = { email: email.trim(), password };

        let session = await dispatch(loginAction(credentials));

        // One retry, a beat later: the account is seconds old and the payment
        // may still be settling against it, and a single refusal on that
        // account would otherwise cost the customer a trip through the login
        // form for no reason. Not retried when the answer was about the
        // credentials themselves - that will not change.
        if (!session?.success && !session?.needsVerification) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          session = await dispatch(loginAction(credentials));
        }

        if (session?.success) {
          // Straight into building the assistant they just paid for. The
          // landing is resolved rather than assumed: an account that somehow
          // has one already belongs on the dashboard.
          const landing = await resolveAuthLanding(dispatch);

          navigate(landing.path, { replace: true, state: landing.state });
          return;
        }

        setStage("");

        // The address has to be verified before it can sign in. The code is
        // already on its way, so this goes to the step that asks for it - and
        // that page signs them in and carries on to the steps.
        if (session?.needsVerification) {
          navigate(RoutesPaths.VERIFY_OTP, {
            replace: true,
            state: {
              email: email.trim(),
              fullName: fullName.trim(),
              notice:
                session.message ||
                "Payment received. Enter the code we emailed you to finish setting up.",
            },
          });
          return;
        }

        // Nothing else to try. The payment went through, so the sign-in form
        // is told as much rather than appearing for no reason.
        navigate(RoutesPaths.LOGIN, {
          replace: true,
          state: { paid: true, email: email.trim() },
        });
        return;
      }

      // Closed the popup, or the payment could not be confirmed. The account
      // exists either way, so signing in is still the way forward.
      setExistingAccount(true);
      setError(
        payment.message ||
          "Your account was created but the payment wasn't completed. Sign in to choose a plan.",
      );
      return;
    }

    setStage("");

    if (!result?.success) {
      // 409: the email already has a running plan. Saying so is the whole
      // help - the page has no session to manage it with.
      setExistingAccount(result?.status === 409);
      setError(result?.message || "We couldn't create your account.");
      return;
    }

    // Created, but nothing to pay - the ordinary signup path, which verifies
    // by email before it has a session.
    navigate(RoutesPaths.VERIFY_OTP, {
      state: {
        fullName: fullName.trim(),
        email: email.trim(),
        plan: planKey,
        interval,
      },
    });
  };

  return (
    <div className="cko">
      <aside className="cko-brand" style={BRAND_BACKGROUND}>
        <img src="/assets/logo.svg" alt="Hostor" className="cko-brand__logo" />

        <span className="cko-brand__rule" aria-hidden="true" />

        <h1 className="cko-brand__title">
          Get started with <span>Hostor today</span>
        </h1>
        <p className="cko-brand__sub">
          Fill in your details to complete your account and continue with your
          selected plan.
        </p>

        <ul className="cko-points">
          {SELLING_POINTS.map((point) => {
            const Icon = point.icon;

            return (
              <li key={point.title}>
                <span className="cko-points__icon">
                  <Icon size={20} strokeWidth={1.9} />
                </span>
                <div>
                  <strong>{point.title}</strong>
                  <span>{point.detail}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      <main className="cko-main">
        <a className="cko-back" href={PRICING_URL}>
          <ArrowLeft size={18} strokeWidth={2.2} />
          Back to plans
        </a>

        <div className="cko-grid">
          <section className="cko-card cko-form">
            <h2 className="cko-card__title">Create your account</h2>
            <p className="cko-card__sub">
              Enter your details to continue with your selected plan.
            </p>

            <form onSubmit={handleSubmit}>
              <label className="cko-field">
                <span className="cko-field__label">Full name</span>
                <span className="cko-input">
                  <User size={17} strokeWidth={1.9} />
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                  />
                </span>
              </label>

              <label className="cko-field">
                <span className="cko-field__label">Work email</span>
                <span className="cko-input">
                  <Mail size={17} strokeWidth={1.9} />
                  <input
                    type="email"
                    placeholder="Enter your work email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </span>
              </label>

              <label className="cko-field">
                <span className="cko-field__label">Password</span>
                <span className="cko-input">
                  <LockKeyhole size={17} strokeWidth={1.9} />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="cko-input__toggle"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff size={17} strokeWidth={1.9} />
                    ) : (
                      <Eye size={17} strokeWidth={1.9} />
                    )}
                  </button>
                </span>
              </label>

              {(error || payError) && (
                <p className="auth-error">{error || payError}</p>
              )}

              {existingAccount && (
                <p className="cko-signin">
                  <button
                    type="button"
                    onClick={() => navigate(RoutesPaths.LOGIN)}
                  >
                    Sign in to manage your plan
                  </button>
                </p>
              )}

              <ul className="cko-assurances">
                <li>
                  <Check size={15} strokeWidth={2.6} />
                  Secure payment
                </li>
                <li>
                  <Check size={15} strokeWidth={2.6} />
                  No credit card required for trial (if applicable)
                </li>
                <li>
                  <Check size={15} strokeWidth={2.6} />
                  Cancel anytime
                </li>
              </ul>

              <button
                type="submit"
                className="cko-submit"
                disabled={Boolean(stage) || blocked}
              >
                {stage ? (
                  <span className="install-status-spinner" aria-hidden="true" />
                ) : null}
                {stage === "account"
                  ? "Setting up your account…"
                  : payStage === "card"
                    ? "Waiting for the card…"
                    : payStage === "confirming"
                      ? "Confirming payment…"
                      : stage === "payment"
                        ? "Opening the card form…"
                        : action?.label || "Continue to payment"}
                {!stage && !blocked && <ArrowRight size={17} strokeWidth={2.4} />}
              </button>

              <p className="cko-terms">
                By creating an account, you agree to our{" "}
                <a href="https://www.hostor.ai/terms/" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://www.hostor.ai/privacy/" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          </section>

          <section className="cko-card cko-plan">
            <h2 className="cko-card__title">Your selected plan</h2>
            <p className="cko-card__sub">
              You can change your plan or billing cycle anytime.
            </p>

            {loadingPlans && !plan ? (
              <div className="cko-plan__box">
                <span className="skel dash-live-skel--title" />
                <span className="skel acct-skel--desc" />
              </div>
            ) : !plan ? (
              <p className="dash-empty">
                We couldn&apos;t find that plan. Go back and pick one.
              </p>
            ) : (
              <>
                <div className="cko-plan__box">
                  <div className="cko-plan__head">
                    <span className="cko-plan__icon">
                      {PlanIcon && <PlanIcon size={24} strokeWidth={1.9} />}
                    </span>

                    <div className="cko-plan__name">
                      <strong>{planLabel(plan.plan)}</strong>
                      {style.blurb && <span>{style.blurb}</span>}
                    </div>

                    <div className="cko-plan__price">
                      <span className="cko-plan__selected">
                        <Check size={13} strokeWidth={3} />
                        Selected plan
                      </span>
                      <p>
                        <strong>{money(perMonth)}</strong>
                        <span>/ month</span>
                      </p>
                      <span className="cko-plan__terms">
                        {annual
                          ? `Billed annually · ${money(plan.monthlyPrice)} billed monthly`
                          : `Billed monthly · ${money(plan.annualPriceMonthly)}/mo billed annually`}
                      </span>
                    </div>
                  </div>

                  <ul className="cko-plan__highlights">
                    {HIGHLIGHTS.map((item, i) => {
                      const text = allowance(plan[item.key], item.one, item.many);
                      if (!text) return null;

                      const Icon = item.icon;

                      return (
                        <li key={`${item.key}-${i}`}>
                          <Icon size={16} strokeWidth={1.9} />
                          {text}
                        </li>
                      );
                    })}
                  </ul>

                  <ul className="cko-plan__details">
                    {DETAILS.map((item) => (
                      <li key={item.key}>
                        <Check size={14} strokeWidth={2.8} />
                        <span>
                          <strong>{formatLimit(plan[item.key])}</strong> {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a className="cko-change" href={PRICING_URL}>
                  <span className="cko-change__icon">
                    <Pencil size={18} strokeWidth={1.9} />
                  </span>
                  <span className="cko-change__text">
                    <strong>Change plan</strong>
                    <span>Not the right fit? Go back and choose a different plan.</span>
                  </span>
                  <ChevronRight size={18} strokeWidth={2} />
                </a>

                <div className="cko-order">
                  <div className="cko-order__head">
                    {/* The quote names itself - "Upgrade summary", "Action
                        blocked" - once there is one to name. */}
                    <h3 className="cko-order__title">
                      {preview?.summaryTitle || typeMeta.title || "Order summary"}
                    </h3>
                    {typeMeta.badge && (
                      <span className={`bill-preview__type is-${typeMeta.tone}`}>
                        {typeMeta.badge}
                      </span>
                    )}
                  </div>

                  {(fromPlan || toPlan) && (
                    <p className="bill-preview__move">
                      <span>
                        {fromPlan?.label || "Current plan"}
                        {fromPlan?.interval && ` (${fromPlan.interval})`}
                      </span>
                      <ArrowRight size={15} strokeWidth={2.2} />
                      <strong>
                        {toPlan?.label || planLabel(plan.plan)}
                        {toPlan?.interval && ` (${toPlan.interval})`}
                      </strong>
                    </p>
                  )}

                  {/* The cycle price as the server states it, and only when it
                      is not the published one already shown above - otherwise
                      the same figure appears twice, once as a row and once as
                      the total. */}
                  {quotedPlanPrice !== null &&
                    quotedPlanPrice !== cycleTotal &&
                    lineItems.length === 0 && (
                      <div className="cko-order__row">
                        <span>
                          {preview.to.label || planLabel(plan.plan)}{" "}
                          {preview.to.interval === "annual" ? "year" : "month"}
                        </span>
                        <span>{money(quotedPlanPrice)}</span>
                      </div>
                    )}

                  {/* The quote's own itemisation when it has one: it carries
                      proration and credits the published price cannot, so the
                      two are never shown side by side. */}
                  {lineItems.length > 0 ? (
                    lineItems.map((item, i) => (
                      <div
                        className={`cko-order__row${
                          Number(item.amount) < 0 ? " is-discount" : ""
                        }`}
                        key={`${item.label}-${i}`}
                      >
                        <span>{item.label}</span>
                        <span>{signedMoney(Number(item.amount))}</span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="cko-order__row">
                        <span>
                          {planLabel(plan.plan)} plan ({annual ? "Annual" : "Monthly"})
                        </span>
                        <span>{money(annual ? listTotal : plan.monthlyPrice)}</span>
                      </div>

                      {/* Only on annual, and only when the tiers actually differ -
                          a plan priced the same either way has nothing to discount. */}
                      {annual && discount > 0 && (
                        <div className="cko-order__row is-discount">
                          <span>Annual discount ({discountPct}%)</span>
                          <span>- {money(discount)}</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Only when the quote breaks it out: without tax the
                      subtotal and the total are the same number. */}
                  {quotedTax !== null && quotedTax !== 0 && (
                    <div className="cko-order__row">
                      <span>
                        Tax
                        {typeof preview?.taxRatePercent === "number" &&
                          preview.taxRatePercent > 0 &&
                          ` (${preview.taxRatePercent}%)`}
                      </span>
                      <span>{money(quotedTax)}</span>
                    </div>
                  )}

                  <div className="cko-order__total">
                    <span>Total today</span>
                    <strong>{money(totalToday)}</strong>
                  </div>
                  <p className="cko-order__note">
                    Billed {annual ? "annually" : "monthly"}
                  </p>

                  {(preview?.effectiveDate || preview?.renewsOn) && (
                    <p className="cko-order__dates">
                      {preview.effectiveDate && (
                        <span>Starts {plainDate(preview.effectiveDate)}</span>
                      )}
                      {preview.effectiveDate && preview.renewsOn && (
                        <span className="cko-order__sep" />
                      )}
                      {preview.renewsOn && (
                        <span>Renews {plainDate(preview.renewsOn)}</span>
                      )}
                    </p>
                  )}

                  {/* Rendered as sent: the backend states the charge better
                      than a sentence composed from the other fields. When it
                      is the reason a change was refused it reads as a warning
                      rather than as a note. */}
                  {preview?.message && (
                    <p
                      className={`cko-order__message${blocked ? " is-blocked" : ""}`}
                      role={blocked ? "alert" : undefined}
                    >
                      {preview.message}
                    </p>
                  )}

                  {/* The workings behind the figures: there when asked for. */}
                  {calculation.length > 0 && (
                    <details className="bill-preview__calc" open>
                      <summary>How is this calculated?</summary>
                      <ul>
                        {calculation.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </>
            )}

            <ul className="cko-trust">
              <li>
                <span className="cko-trust__icon">
                  <LockKeyhole size={17} strokeWidth={1.9} />
                </span>
                <span>
                  <strong>Secure payment</strong>
                  SSL encrypted
                </span>
              </li>
              <li>
                <span className="cko-trust__icon">
                  <BadgeCheck size={17} strokeWidth={1.9} />
                </span>
                <span>
                  <strong>30-day guarantee</strong>
                  Money back if not satisfied
                </span>
              </li>
              <li>
                <span className="cko-trust__icon">
                  <CreditCard size={17} strokeWidth={1.9} />
                </span>
                <span>
                  <strong>All major cards</strong>
                  Your card details never touch our servers
                </span>
              </li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}
